import { MAIN_PRIZE_STRUCTURE_CODE } from '@daymade/common-lib/constants';
import { PrizeWinStatus } from '@daymade/common-lib/dbEnums';
import DashboardComponentHeader from 'components/dashboard/common/DashboardComponentHeader';
import DashboardComponentLink from 'components/dashboard/common/DashboardComponentLink';
import {
  isClaimedPrizeWin,
  isClaimedPromoPrizeReward,
  isLoadedPrizeWins,
  isUnclaimedPromoPrizeReward,
  TPrizeDisplayItem,
} from 'constants/WinType';
import { unclaimedPrizeWinStatuses } from 'hooks/account/useTreatWins';
import { useUserStore } from 'hooks/stores/useUserStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { TUnclaimedPrizeWins } from 'stores/UserStore';
import { LOADING_TIMEOUT } from '../common/constants';
import { ClaimedPrize } from './ClaimedPrize';
import { UnclaimedPrize } from './UnclaimedPrize';
import styles from './YourWins.module.css';

interface YourWinsProps {
  unclaimedPrizeWins: TUnclaimedPrizeWins;
  getPrizeWins: () => Promise<void>;
  allWins: TPrizeDisplayItem[];
  loading?: boolean;
}

const YourWins = observer(
  ({ unclaimedPrizeWins, getPrizeWins, allWins, loading }: YourWinsProps) => {
    const { initialized } = useUserStore();
    const { isMobile } = useIsMobile();
    const [prizeDisplay, setPrizeDisplay] = useState<
      TPrizeDisplayItem[] | null
    >(null);
    const [yourWinsLoading, setYourWinsLoading] = useState(true);

    useEffect(() => {
      if (initialized && loading === undefined) {
        return setYourWinsLoading(false);
      }

      if (initialized && !loading) {
        setTimeout(() => {
          setYourWinsLoading(false);
        }, LOADING_TIMEOUT);
      }
    }, [initialized, loading]);

    useEffect(() => {
      if (allWins && allWins?.length > 0) {
        setPrizeDisplay(allWins.slice(0, 3));
        return;
      }

      setPrizeDisplay(allWins ?? []); // if there are no wins, we set prizeDisplay to an empty array, which will cause the component to render nothing
    }, [allWins]);

    if (prizeDisplay === null || prizeDisplay.length === 0) {
      return null;
    }

    const nonExpiredUnclaimedPrizeWins =
      unclaimedPrizeWins !== 'LOADING' && unclaimedPrizeWins !== 'ERROR'
        ? unclaimedPrizeWins.filter(
            (win) =>
              'expiresAt' in win && win.expiresAt && win.expiresAt > new Date(),
          )
        : [];

    const WinSkeleton = () => (
      <Skeleton
        width={'100%'}
        height={isMobile ? 100 : 132}
        containerClassName="flex-1"
        borderRadius={18}
      />
    );

    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <DashboardComponentHeader
            loading={yourWinsLoading}
            title={'Your wins'}
            banner={
              yourWinsLoading ? (
                <Skeleton width={150} height={30} borderRadius={12} />
              ) : (
                isLoadedPrizeWins(unclaimedPrizeWins) &&
                nonExpiredUnclaimedPrizeWins.length > 0 && (
                  <span className={styles.badge}>
                    {nonExpiredUnclaimedPrizeWins.length} UNCLAIMED
                  </span>
                )
              )
            }
            separator
          />

          <div className={styles.prizeGrid}>
            {yourWinsLoading
              ? Array.from(Array(3)).map(() => <WinSkeleton />)
              : prizeDisplay.map((prize) => {
                  if (
                    'expiresAt' in prize &&
                    unclaimedPrizeWinStatuses.includes(
                      prize.status as PrizeWinStatus,
                    )
                  ) {
                    const prizeIsPromo = isUnclaimedPromoPrizeReward(prize);
                    const prizeType = prizeIsPromo
                      ? 'CONSOLATION_PRIZE'
                      : prize?.prizeStructureCode === MAIN_PRIZE_STRUCTURE_CODE
                        ? 'MAIN_DRAW'
                        : prize.prizeTierCode === 'MONTHLY_GIVEAWAY'
                          ? 'MONTHLY_GIVEAWAY'
                          : 'TOKEN_TOWN';

                    return (
                      <UnclaimedPrize
                        key={prize?.id}
                        prizeWinId={prize.id}
                        drawName={!prizeIsPromo ? prize.drawName : ''}
                        drawId={!prizeIsPromo ? prize.drawId : undefined}
                        prizeTierCode={
                          !prizeIsPromo ? prize.prizeTierCode : prize.code
                        }
                        prizeImageLocation={
                          !prizeIsPromo
                            ? prize?.drawPrizeImageLocation || ''
                            : ''
                        }
                        prizeType={prizeType}
                        createdAt={prize.createdAt}
                        expiresAt={prize.expiresAt!}
                        drawPrizeSlug={
                          !prizeIsPromo
                            ? prize.drawPrizeSlug || undefined
                            : undefined
                        }
                        onPrizeClaimed={getPrizeWins}
                        prizeName={prizeIsPromo ? prize.prizeTitle || '' : ''}
                      />
                    );
                  }

                  if (
                    !unclaimedPrizeWinStatuses.includes(
                      prize.status as PrizeWinStatus,
                    ) &&
                    (isClaimedPrizeWin(prize) ||
                      isClaimedPromoPrizeReward(prize))
                  ) {
                    const prizeIsPromo = isClaimedPromoPrizeReward(prize);
                    return (
                      <ClaimedPrize
                        key={prize.id}
                        className={styles.prizeGridItem}
                        prizeWinId={prize.id}
                        prizeStructureCode={
                          !prizeIsPromo ? prize.prizeStructureCode : ''
                        }
                        chosenAt={!prizeIsPromo ? prize.chosenAt : null}
                        chosenPrizeName={
                          !prizeIsPromo
                            ? prize.chosenPrizeName
                            : prize.prizeTitle
                        }
                        prizeTierCode={
                          !prizeIsPromo ? prize.prizeTierCode : prize.code
                        }
                        createdAt={prize.createdAt}
                        chosenPrizeImageLocation={
                          !prizeIsPromo
                            ? prize.chosenPrizeImageLocation
                            : prize.prizeImageLocation
                        }
                      />
                    );
                  }

                  return null;
                })}
          </div>

          <DashboardComponentLink
            loading={yourWinsLoading}
            linkText={'View all'}
            href={'/my-wins'}
          />
        </div>
      </div>
    );
  },
);

export default YourWins;
